import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material';

import { useAuth } from 'contexts/auth';
import { useViewport } from 'contexts/viewport';
import withResponsiveLayout from 'hoc/withResponsive';
import { Box, Button, DotProgression, ResponsiveDialog, Stack, Text } from 'shell/components';
import { NICKEL_TOUR_RESOURCES } from 'utils/helper';
import { useTour } from 'contexts/tour';

const renderTitle = (title, coloredTitle, tailTitle = '') => {
    return (
        <Text variant={'h4'}>
            {title}
            <Text color={'primary'} component={'span'} variant={'h4'}>
                {coloredTitle}
            </Text>
            {tailTitle}
        </Text>
    );
};

const renderDescription = (description) => {
    return <Text variant={'p2'}>{description}</Text>;
};

const STEP_COMPONENTS = {
    0: {
        graphicLeft: (device) => NICKEL_TOUR_RESOURCES[device][0].left,
        graphicRight: (device) => NICKEL_TOUR_RESOURCES[device][0].right,
        title: renderTitle('Welcome to', ' Ensightful!'),
        description: renderDescription(
            'Ensightful provides you with powerful tools to help you seamlessly collaborate on your group projects! The platform is specifically designed to empower you with valuable project management skills that are in high demand across a variety of industries.'
        ),
        buttonText: 'Start',
    },
    1: {
        graphicLeft: (device) => NICKEL_TOUR_RESOURCES[device][1].left,
        graphicRight: (device) => NICKEL_TOUR_RESOURCES[device][1].right,
        title: renderTitle('Your', ' Project Timeline'),
        description: renderDescription(
            'Timeline View helps you to simplify complex projects by breaking them down into smaller, more manageable tasks. It provides a visual representation of your teams’ tasks, milestones and progress, ensuring the timely completion of all deliverables.'
        ),
        buttonText: 'Got it!',
    },
    2: {
        graphicLeft: (device) => NICKEL_TOUR_RESOURCES[device][2].left,
        graphicRight: (device) => NICKEL_TOUR_RESOURCES[device][2].right,
        title: renderTitle('Customize', ' Notifications'),
        description: renderDescription(
            'You can customize which notifications you want to receive, and how frequently you will receive them in notification settings. Choose the ones that work best for you!'
        ),
        buttonText: 'Sounds Good',
    },
    3: {
        graphicLeft: (device) => NICKEL_TOUR_RESOURCES[device][3].left,
        graphicRight: (device) => NICKEL_TOUR_RESOURCES[device][3].right,
        title: renderTitle('Why', ' Project Management ', 'Matters'),
        description: renderDescription(
            'By learning from and showcasing your experience with Ensightful, you demonstrate to potential employers that you possess the practical skills and experience necessary to excel in a wide variety of roles, significantly increasing your chances of securing desirable job opportunities after graduation.'
        ),
        buttonText: 'Got it!',
    },
};

const Mobile = ({ step, device, stepNumber, handleNext }) => {
    return (
        <NickelMobileBox>
            <NickelMobileTopStack>{STEP_COMPONENTS[step].graphicRight(device)}</NickelMobileTopStack>
            <NickelMobileBottomStack alignItems={'center'} justifyContent={'space-between'}>
                <NickelLeftComponentStack alignItems={'center'} justifyContent={'space-between'}>
                    <NickelMobileTextStack
                        alignSelf={'center'}
                        alignItems={'center'}
                        justifyContent={'center'}
                        spacing={2}
                    >
                        {STEP_COMPONENTS[step].title}
                        {STEP_COMPONENTS[step].description}
                    </NickelMobileTextStack>
                    <Stack alignItems={'center'} spacing={8}>
                        <Button variant={'primary'} onClick={() => handleNext()}>
                            {STEP_COMPONENTS[step].buttonText}
                        </Button>
                        <DotProgression activeStepIndex={step} numSteps={stepNumber} />
                    </Stack>
                </NickelLeftComponentStack>
            </NickelMobileBottomStack>
        </NickelMobileBox>
    );
};

const Tablet = ({ step, device, stepNumber, handleNext }) => {
    return (
        <NickelBox device={device}>
            <Stack sx={{ height: '100%' }} direction={'row'}>
                <NickelLeftBox device={device}>
                    <NickelLeftInnerBox device={device}>{STEP_COMPONENTS[step].graphicLeft(device)}</NickelLeftInnerBox>
                    <NickelLeftComponentStack alignItems={'center'} justifyContent={'space-between'}>
                        <NickelLeftTextStack
                            alignSelf={'center'}
                            alignItems={'center'}
                            device={device}
                            justifyContent={'center'}
                            spacing={2}
                        >
                            {STEP_COMPONENTS[step].title}
                            {STEP_COMPONENTS[step].description}
                        </NickelLeftTextStack>
                        <Stack alignItems={'center'} spacing={8}>
                            <Button variant={'primary'} onClick={() => handleNext()}>
                                {STEP_COMPONENTS[step].buttonText}
                            </Button>
                            <DotProgression activeStepIndex={step} numSteps={stepNumber} />
                        </Stack>
                    </NickelLeftComponentStack>
                </NickelLeftBox>
                <NickelRightBox device={device}>{STEP_COMPONENTS[step].graphicRight(device)} </NickelRightBox>
            </Stack>
        </NickelBox>
    );
};

const Desktop = ({ step, device, stepNumber, handleNext }) => {
    return (
        <NickelBox device={device}>
            <Stack sx={{ height: '100%' }} direction={'row'}>
                <NickelLeftBox device={device}>
                    <NickelLeftInnerBox device={device}>{STEP_COMPONENTS[step].graphicLeft(device)}</NickelLeftInnerBox>
                    <NickelLeftComponentStack alignItems={'center'} justifyContent={'space-between'}>
                        <NickelLeftTextStack
                            alignSelf={'center'}
                            alignItems={'center'}
                            device={device}
                            justifyContent={'center'}
                            spacing={2}
                        >
                            {STEP_COMPONENTS[step].title}
                            {STEP_COMPONENTS[step].description}
                        </NickelLeftTextStack>
                        <Stack alignItems={'center'} spacing={8}>
                            <Button variant={'primary'} onClick={() => handleNext()}>
                                {STEP_COMPONENTS[step].buttonText}
                            </Button>
                            <DotProgression activeStepIndex={step} numSteps={stepNumber} />
                        </Stack>
                    </NickelLeftComponentStack>
                </NickelLeftBox>
                <NickelRightBox device={device}>{STEP_COMPONENTS[step].graphicRight(device)} </NickelRightBox>
            </Stack>
        </NickelBox>
    );
};

const LATEST_UPDATE_DATE = new Date('2023-07-27').getTime();

const NickelTour = () => {
    const { device, width } = useViewport();
    const { currentUser, updateCurrentUser } = useAuth();
    const { tourOn } = useTour();

    const [step, setStep] = useState(0);
    const [openNickelTour, setOpenNickelTour] = useState(false);

    const stepNumber = Object.keys(STEP_COMPONENTS).length;

    const handleNext = () => {
        if (STEP_COMPONENTS[step + 1]) {
            return setStep(step + 1);
        }
        updateCurrentUser({ tour: { ...currentUser?.tour, nickel_tour: new Date().getTime() } });
        return setOpenNickelTour(false);
    };

    useEffect(() => {
        if (currentUser && !tourOn) {
            if (currentUser.tour?.nickel_tour > LATEST_UPDATE_DATE) {
                setOpenNickelTour(false);
            } else {
                setOpenNickelTour(true);
            }
        }
    }, [currentUser, tourOn]);

    const props = {
        step,
        device,
        stepNumber,
        handleNext,
    };

    const responsiveLayouts = {
        mobile: <Mobile {...props} />,
        tablet: <Tablet {...props} />,
        desktop: <Desktop {...props} />,
    };

    return (
        <CustomDialog sx={{ padding: 0 }} device={device} open={openNickelTour}>
            {withResponsiveLayout(width, responsiveLayouts)}
        </CustomDialog>
    );
};

const CustomDialog = styled(ResponsiveDialog)(({ device }) => {
    return {
        '& .MuiBox-root': {
            padding: 0,
        },
        ...(device === 'mobile' && {
            '& .MuiPaper-root > .MuiBox-root': {
                height: '100%',
            },
        }),
    };
});

const NickelBox = styled(Box)(({ device }) => {
    return {
        padding: 0,
        overflow: 'hidden',

        ...(device === 'desktop' && { width: '957px', height: '487px' }),
        ...(device === 'tablet' && { width: '736px', height: '487px' }),
    };
});

const NickelLeftBox = styled(Box)(({ device }) => {
    return {
        height: '100%',
        ...(device === 'desktop' && { width: '444px' }),
        ...(device === 'tablet' && { width: '368px' }),
    };
});

const NickelLeftInnerBox = styled(Box)(({ device, theme }) => {
    return {
        position: 'absolute',
        ...(device === 'desktop' && { margin: theme.spacing(-10, 0, 0, -5) }),
        ...(device === 'tablet' && { margin: theme.spacing(-5, 0, 0, -5) }),
    };
});

const NickelLeftComponentStack = styled(Stack)(({ theme }) => {
    return {
        paddingBottom: theme.spacing(8),
        height: '100%',
    };
});

const NickelLeftTextStack = styled(Stack)(({ device, theme }) => {
    return {
        textAlign: 'center',
        ...(device === 'desktop' && { width: theme.spacing(95), marginTop: theme.spacing(35) }),
        ...(device === 'tablet' && { width: theme.spacing(76), marginTop: theme.spacing(24) }),
    };
});

const NickelRightBox = styled(Box)(({ device }) => {
    return {
        padding: 0,
        height: '100%',
        ...(device === 'desktop' && { width: '513px' }),
        ...(device === 'tablet' && { width: '368px' }),
    };
});

const NickelMobileBox = styled(Box)(() => {
    return {
        padding: 0,
        width: '100%',
        height: '100%',
    };
});

const NickelMobileTopStack = styled(Stack)(() => {
    return {
        height: '50%',
    };
});

const NickelMobileBottomStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(8, 4),
        height: '50%',
    };
});

const NickelMobileTextStack = styled(Stack)(() => {
    return {
        textAlign: 'center',
        width: '80%',
    };
});

export default NickelTour;
